import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { UnitTextField } from '@/common/components/UnitTextField'
import { unities, unitiesDecimals } from '@/common/constants/unities.constant'
import { formatDecimalUnit } from '@/common/utils/formatters'
import { unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { GovernmentAllocationEntryProductCategoryQuantityValidationSchema } from '@/features/privateCompanies/pages/CompanyGovernmentAllocations/schemas/update.schema'
import { ClientCompanyAllowedCategoryAndProductData } from '@/features/privateCompanies/types/allowedCategoriesAndProducts.type'
import { CompanyGovernmentAllocationEntryProduct } from '@/features/privateCompanies/types/companyGovernmentAllocationEntry.type'
import { FileBoxIcon, Trash2Icon } from 'lucide-react'
import { useFormContext, useWatch } from 'react-hook-form'

interface EntryProductFormItemQuantityProps {
  entryProductIndex: number
  entryProduct: CompanyGovernmentAllocationEntryProduct
  onDelete: () => void
  disabled: boolean
  allowedProductSummary: ClientCompanyAllowedCategoryAndProductData
  onTotalQuantityChange: () => void
}

export const EntryProductFormItemQuantity = ({
  entryProductIndex,
  entryProduct,
  onDelete,
  disabled,
  allowedProductSummary,
  onTotalQuantityChange,
}: EntryProductFormItemQuantityProps) => {
  const {
    control,
    register,
    formState: { errors },
  } = useFormContext<GovernmentAllocationEntryProductCategoryQuantityValidationSchema>()

  const watchCurrentProductQuantity = useWatch({
    control,
    name: `entry_products.${entryProductIndex}.total_quantity`,
  })

  const availableQuantity = Number(allowedProductSummary?.available_quantity) || 0
  const availableForAllocation = Math.max(
    availableQuantity - Number(unFormatUnknownUnit(watchCurrentProductQuantity) || 0),
    0,
  )

  return (
    <Box className="flex flex-col gap-4 p-4">
      <div className="flex justify-between">
        <div className="flex items-center gap-x-2">
          <FileBoxIcon strokeWidth={1} />

          <p>{entryProduct?.product?.display_name}</p>
        </div>

        <Button
          type="button"
          variant="outline-error"
          className="size-10 p-0 text-danger"
          onClick={onDelete}
          disabled={disabled}
        >
          <Trash2Icon strokeWidth={1} />
        </Button>
      </div>

      <div className="flex flex-col gap-1">
        <UnitTextField
          label="Quantidade total"
          unit={entryProduct.product.unit_for_quantity ? unities[entryProduct.product.unit_for_quantity] : undefined}
          decimals={entryProduct.product.unit_for_quantity ? unitiesDecimals[entryProduct.product.unit_for_quantity] : 2}
          autoCorrect="off"
          {...register(`entry_products.${entryProductIndex}.total_quantity`)}
          onChange={(event) => {
            register(`entry_products.${entryProductIndex}.total_quantity`).onChange(event);
            onTotalQuantityChange();
          }}
          value={watchCurrentProductQuantity}
          forceValue
          errorMessage={errors.entry_products?.[entryProductIndex]?.total_quantity?.message || undefined}
          disabled={disabled}
          markAsRequired
        />

        {allowedProductSummary && (
          <div className="flex gap-3">
            <p className="italic text-xs text-white-400">
              Quantidade possível:{' '}
              {allowedProductSummary?.available_quantity
                ? formatDecimalUnit(Number(allowedProductSummary?.available_quantity), allowedProductSummary.unit_for_quantity)
                : '--'}
            </p>

            <p className="italic text-xs text-white-400">
              Disponível para distribuir: {formatDecimalUnit(Number(availableForAllocation), allowedProductSummary.unit_for_quantity)}
            </p>

            <p className="italic text-xs text-white-400">
              Quantidade garantida:{' '}
              {allowedProductSummary?.granted_quantity
                ? formatDecimalUnit(Number(allowedProductSummary?.granted_quantity), allowedProductSummary.unit_for_quantity)
                : '--'}
            </p>

            <p className="italic text-xs text-white-400">
              Quantidade inserida:{' '}
              {allowedProductSummary?.entry_quantity
              ? formatDecimalUnit(Number(allowedProductSummary?.entry_quantity), allowedProductSummary.unit_for_quantity)
              : '--'}
            </p>
          </div>
        )}
      </div>
    </Box>
  )
}
